import {
  memo,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  useCallback,
  useRef,
  useState,
} from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { PERCENT_SCALE } from '@air/utilities';
import { cn } from '@air/utils-tailwind';

import {
  getImageMinimapSize,
  IMAGE_MINIMAP_MAX_EDGE_PX,
  type ImageMinimapSize,
} from '~/components/AssetModalV2/Visualizer/ImageVisualizer/getImageMinimapSize';
import {
  getImageMinimapViewport,
  type ImageMinimapViewport,
} from '~/components/AssetModalV2/Visualizer/ImageVisualizer/getImageMinimapViewport';

const KEYBOARD_PAN_STEP = 0.03;
const KEYBOARD_PAN_STEP_FAST = 0.1;
const CENTER_FRACTION = 0.5;
const FULL_VIEWPORT_FRACTION = 1;

export type ImageMinimapProps = {
  className?: string;
  'data-testid'?: string;
  imageRef: RefObject<HTMLImageElement | null>;
  imageSrc: string;
  imgContainerRef: RefObject<HTMLDivElement | null>;
  minimapId: string;
  onMinimapFocusHandled: () => void;
  onPanToImageFraction: (params: { transitionDuration?: number; x: number; y: number }) => void;
  posX: number;
  posY: number;
  shouldFocus: boolean;
  zoom: number;
};

type MinimapDragState = {
  offsetX: number;
  offsetY: number;
  pointerId: number;
};

const areSizesEqual = (first: ImageMinimapSize | null, second: ImageMinimapSize | null) =>
  first?.height === second?.height && first?.width === second?.width;

const areViewportsEqual = (first: ImageMinimapViewport | null, second: ImageMinimapViewport | null) =>
  first?.height === second?.height &&
  first?.left === second?.left &&
  first?.top === second?.top &&
  first?.width === second?.width;

const clampFraction = (value: number) => Math.min(1, Math.max(0, value));

const clampViewportCenter = (value: number, viewportExtent: number) => {
  const halfViewportExtent = viewportExtent / 2;
  return Math.min(FULL_VIEWPORT_FRACTION - halfViewportExtent, Math.max(halfViewportExtent, value));
};

const getPanAnnouncement = ({ viewport, x, y }: { viewport: ImageMinimapViewport | null; x: number; y: number }) => {
  if (viewport?.height === FULL_VIEWPORT_FRACTION && viewport.width === FULL_VIEWPORT_FRACTION) {
    return 'Image fully visible';
  }

  return `Image position ${Math.round(x * PERCENT_SCALE)} percent horizontal, ${Math.round(
    y * PERCENT_SCALE,
  )} percent vertical`;
};

const getKeyboardPanDelta = (key: string, step: number) => {
  if (key === 'ArrowLeft') {
    return { x: -step, y: 0 };
  }

  if (key === 'ArrowRight') {
    return { x: step, y: 0 };
  }

  if (key === 'ArrowUp') {
    return { x: 0, y: -step };
  }

  if (key === 'ArrowDown') {
    return { x: 0, y: step };
  }

  return { x: 0, y: 0 };
};

export const ImageMinimap = memo(
  ({
    className,
    'data-testid': dataTestId = 'IMAGE_MINIMAP',
    imageRef,
    imageSrc,
    imgContainerRef,
    minimapId,
    onMinimapFocusHandled,
    onPanToImageFraction,
    posX,
    posY,
    shouldFocus,
    zoom,
  }: ImageMinimapProps) => {
    const frameRef = useRef<HTMLButtonElement>(null);
    const dragRef = useRef<MinimapDragState | null>(null);
    const viewStateRef = useRef({ posX, posY, zoom });
    const [announcement, setAnnouncement] = useState('');
    const [isDragging, setIsDragging] = useState(false);
    const [size, setSize] = useState<ImageMinimapSize | null>(null);
    const [viewport, setViewport] = useState<ImageMinimapViewport | null>(null);
    const minimapInstructionsId = `${minimapId}-instructions`;

    const updateMinimapGeometry = useCallback(() => {
      const image = imageRef.current;
      const container = imgContainerRef.current?.parentElement;

      if (!image || !container || !imageSrc) {
        setSize((current) => (current === null ? current : null));
        setViewport((current) => (current === null ? current : null));
        return;
      }

      const nextSize = getImageMinimapSize({
        maxEdge: IMAGE_MINIMAP_MAX_EDGE_PX,
        naturalHeight: image.naturalHeight || image.getBoundingClientRect().height,
        naturalWidth: image.naturalWidth || image.getBoundingClientRect().width,
      });
      setSize((current) => (areSizesEqual(current, nextSize) ? current : nextSize));

      const { posX: currentPosX, posY: currentPosY, zoom: currentZoom } = viewStateRef.current;

      if (currentZoom <= 0) {
        setViewport((current) => (current === null ? current : null));
        return;
      }

      const imageRect = image.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const nextViewport = getImageMinimapViewport({
        containerHeight: containerRect.height,
        containerWidth: containerRect.width,
        fitHeight: imageRect.height / currentZoom,
        fitWidth: imageRect.width / currentZoom,
        posX: currentPosX,
        posY: currentPosY,
        zoom: currentZoom,
      });
      setViewport((current) => (areViewportsEqual(current, nextViewport) ? current : nextViewport));
    }, [imageRef, imageSrc, imgContainerRef]);

    useIsomorphicLayoutEffect(() => {
      const image = imageRef.current;
      const container = imgContainerRef.current?.parentElement;

      if (!image || !container) {
        updateMinimapGeometry();
        return;
      }

      updateMinimapGeometry();
      image.addEventListener('load', updateMinimapGeometry);

      const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateMinimapGeometry);
      resizeObserver?.observe(container);
      resizeObserver?.observe(image);

      return () => {
        image.removeEventListener('load', updateMinimapGeometry);
        resizeObserver?.disconnect();
      };
    }, [imageRef, imgContainerRef, updateMinimapGeometry]);

    useIsomorphicLayoutEffect(() => {
      viewStateRef.current = { posX, posY, zoom };
      updateMinimapGeometry();
    }, [posX, posY, updateMinimapGeometry, zoom]);

    useIsomorphicLayoutEffect(() => {
      const frame = frameRef.current;

      if (!shouldFocus || !frame || !size) {
        return;
      }

      frame.focus({ preventScroll: true });
      onMinimapFocusHandled();
    }, [onMinimapFocusHandled, shouldFocus, size]);

    const panToClientPoint = useCallback(
      (clientX: number, clientY: number, offsetX = 0, offsetY = 0) => {
        const frame = frameRef.current;

        if (!frame || !viewport) {
          return;
        }

        const rect = frame.getBoundingClientRect();

        if (rect.width <= 0 || rect.height <= 0) {
          return;
        }

        const rawX = (clientX - rect.left) / rect.width - offsetX;
        const rawY = (clientY - rect.top) / rect.height - offsetY;

        onPanToImageFraction({
          transitionDuration: 0,
          x: clampFraction(rawX + viewport.width / 2),
          y: clampFraction(rawY + viewport.height / 2),
        });
      },
      [onPanToImageFraction, viewport],
    );

    const handleFramePointerDown = useCallback(
      (event: ReactPointerEvent<HTMLButtonElement>) => {
        if (event.button !== 0) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.focus({ preventScroll: true });

        if (!viewport) {
          return;
        }

        const rect = event.currentTarget.getBoundingClientRect();

        if (rect.width <= 0 || rect.height <= 0) {
          return;
        }

        const pointerX = (event.clientX - rect.left) / rect.width;
        const pointerY = (event.clientY - rect.top) / rect.height;
        const isOnViewport =
          pointerX >= viewport.left &&
          pointerX <= viewport.left + viewport.width &&
          pointerY >= viewport.top &&
          pointerY <= viewport.top + viewport.height;
        const offsetX = isOnViewport ? pointerX - viewport.left : viewport.width / 2;
        const offsetY = isOnViewport ? pointerY - viewport.top : viewport.height / 2;

        dragRef.current = { offsetX, offsetY, pointerId: event.pointerId };
        setIsDragging(true);
        event.currentTarget.setPointerCapture(event.pointerId);
        panToClientPoint(event.clientX, event.clientY, offsetX, offsetY);
      },
      [panToClientPoint, viewport],
    );

    const handleFramePointerMove = useCallback(
      (event: ReactPointerEvent<HTMLButtonElement>) => {
        const drag = dragRef.current;

        if (!drag || drag.pointerId !== event.pointerId) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        panToClientPoint(event.clientX, event.clientY, drag.offsetX, drag.offsetY);
      },
      [panToClientPoint],
    );

    const handleFramePointerEnd = useCallback((event: ReactPointerEvent<HTMLButtonElement>) => {
      const drag = dragRef.current;

      if (!drag || drag.pointerId !== event.pointerId) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      dragRef.current = null;
      setIsDragging(false);

      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    }, []);

    const handleKeyDown = useCallback(
      (event: ReactKeyboardEvent<HTMLButtonElement>) => {
        if (!['ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'Home'].includes(event.key)) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        event.nativeEvent.stopImmediatePropagation();

        const currentCenterX = viewport ? viewport.left + viewport.width / 2 : CENTER_FRACTION;
        const currentCenterY = viewport ? viewport.top + viewport.height / 2 : CENTER_FRACTION;
        const step = event.shiftKey ? KEYBOARD_PAN_STEP_FAST : KEYBOARD_PAN_STEP;
        const delta = getKeyboardPanDelta(event.key, step);
        const requestedCenter =
          event.key === 'Home'
            ? { x: CENTER_FRACTION, y: CENTER_FRACTION }
            : {
                x: clampFraction(currentCenterX + delta.x),
                y: clampFraction(currentCenterY + delta.y),
              };
        const nextCenter = viewport
          ? {
              x: clampViewportCenter(requestedCenter.x, viewport.width),
              y: clampViewportCenter(requestedCenter.y, viewport.height),
            }
          : requestedCenter;

        onPanToImageFraction({ transitionDuration: 0, ...nextCenter });
        setAnnouncement(getPanAnnouncement({ viewport, ...nextCenter }));
      },
      [onPanToImageFraction, viewport],
    );

    const handleClick = useCallback(
      (event: ReactMouseEvent<HTMLButtonElement>) => {
        if (event.detail !== 0) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        const center = { x: CENTER_FRACTION, y: CENTER_FRACTION };
        onPanToImageFraction({ transitionDuration: 0, ...center });
        setAnnouncement(getPanAnnouncement({ viewport, ...center }));
      },
      [onPanToImageFraction, viewport],
    );

    if (!size) {
      return null;
    }

    return (
      <button
        aria-describedby={minimapInstructionsId}
        aria-label="Image minimap"
        className={cn(
          'absolute bottom-14 left-3 z-[5] touch-none overflow-hidden rounded-8 border border-white/25 bg-black/80 p-0 shadow-popover outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black',
          isDragging ? 'cursor-grabbing' : 'cursor-grab',
          className,
        )}
        data-image-minimap=""
        data-minimap-image-frame=""
        data-testid={dataTestId}
        id={minimapId}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onLostPointerCapture={handleFramePointerEnd}
        onPointerCancel={handleFramePointerEnd}
        onPointerDown={handleFramePointerDown}
        onPointerMove={handleFramePointerMove}
        onPointerUp={handleFramePointerEnd}
        ref={frameRef}
        style={{ height: size.height, width: size.width }}
        type="button"
      >
        <span className="sr-only" id={minimapInstructionsId}>
          Use Arrow keys to pan the image. Hold Shift to pan faster. Press Home to center the image.
        </span>
        <span aria-live="polite" className="sr-only">
          {announcement}
        </span>
        {/* biome-ignore lint/performance/noImgElement: the minimap mirrors the already-loaded visualizer image. */}
        <img
          aria-hidden="true"
          alt=""
          className="pointer-events-none block size-full select-none object-cover"
          src={imageSrc}
        />
        {viewport ? (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute rounded-4 border-2 border-white"
            style={{
              boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.75), 0 0 0 9999px rgba(0, 0, 0, 0.22)',
              height: `${viewport.height * PERCENT_SCALE}%`,
              left: `${viewport.left * PERCENT_SCALE}%`,
              top: `${viewport.top * PERCENT_SCALE}%`,
              width: `${viewport.width * PERCENT_SCALE}%`,
            }}
          />
        ) : null}
      </button>
    );
  },
);

ImageMinimap.displayName = 'ImageMinimap';
